import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { MetaService } from '@core/services/meta.service';
import { OpenGraphService } from '@core/services/open-graph.service';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { SiteContextService } from '@core/services/site-context.service';
import { UrlHelperService } from '@core/services/url-helper.service';
import { environment } from '@environments/environment';
import { getLazyPageComponent } from '@features/page/page-components';
import { PageTypes } from '@features/page/page.config';
import { GenericPageComponent, UmbracoStandardPageModel } from '@features/page/page.models';
import { BreadcrumbService } from '@features/layout/breadcrumb/breadcrumb.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { LoggingService } from '@core/services/logging.service';
import { UmbracoBlockListModule } from '@features/umbraco-block-list/umbraco-block-list.module';
import { UntilDestroy } from '@ngneat/until-destroy';

/**
 * PageComponent is the main component responsible for providing data to each page component.
 * It's essentially the parent of any page component.
 */
@UntilDestroy()
@Component({
    imports: [UmbracoBlockListModule],
    standalone: true,
    template: ``,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponent implements OnInit, OnDestroy {
    private readonly vcr = inject(ViewContainerRef);

    private isDestroyed = false;
    public component?: GenericPageComponent<any>;
    public inputs: { data?: UmbracoStandardPageModel } = {};

    constructor(
        private readonly logging: LoggingService,
        private readonly siteContextService: SiteContextService,
        private readonly breadcrumbService: BreadcrumbService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly urlHelper: UrlHelperService,
        private readonly openGraphService: OpenGraphService,
        private readonly title: Title,
        private readonly router: Router,
        private readonly urlHelperService: UrlHelperService,
        private readonly metaService: MetaService,
        private readonly relewiseTrackingService: RelewiseTrackingService,
        private readonly gtmService: GtmService
    ) {}

    public ngOnDestroy(): void {
        this.isDestroyed = true;
        this.vcr?.clear();
    }

    public ngOnInit() {
        const data: UmbracoStandardPageModel = this.activatedRoute.snapshot.data.content;

        // Check if the page contains a redirect information
        // If the redirect was manually added in the CMS, we don't
        // have any other way to handle it
        if (data.redirectToUrlInformation) {
            const redirectToUrlInformation = data.redirectToUrlInformation;
            if (redirectToUrlInformation?.destinationPage) {
                const redirectPath = this.router.parseUrl(redirectToUrlInformation?.destinationPage?.url ?? '');
                this.router.navigateByUrl(redirectPath, {});
                return;
            }
        }

        if (!data) {
            console.warn(`Page Error: Page "${this.urlHelper.getCurrentUrl()}" has no content`);
            return;
        }

        this.renderComponent(data);
    }

    private async renderComponent(data: UmbracoStandardPageModel) {
        const template = data.contentTypeAlias ?? '';

        this.siteContextService.setContext({ type: template, key: data.key ?? '', pageId: data.id });

        if (data.breadCrumbs) {
            const breadcrumbs = data.breadCrumbs.map(item => ({ name: item.name ?? '', url: item.url ?? '' }));
            this.breadcrumbService.setBreadcrumb(breadcrumbs);
        }

        const component: GenericPageComponent<any> = await getLazyPageComponent(template || 'UNDEFINED');

        if (!component) {
            const errorMsg = `No matching page component found for "${data.contentTypeAlias ?? 'UNDEFINED'}" on "${this.urlHelper.getCurrentUrl()}"`;
            this.logging.logMessage('Page Error', errorMsg);
            throw Error(errorMsg);
        }

        // hack to avoid having error
        if (this.isDestroyed) {
            return;
        }

        if (this.vcr) {
            // the component factory was deprecated since version 13, so now we can inject directly the component to the
            // view container reference, please see more here https://angular.io/api/core/ComponentFactoryResolver#description
            const componentRef = this.vcr.createComponent<GenericPageComponent<any>>(component);
            componentRef.instance.data = data;
            componentRef.changeDetectorRef.markForCheck();
        }

        const pageUrl = data.pageUrl
            ? `${this.urlHelperService.getOrigin()}${data.pageUrl.url}`
            : `${this.urlHelperService.getOrigin()}${this.urlHelperService.getCurrentUrl().pathname}`;

        // Open Graph
        this.openGraphService.openGraphMeta(
            'set',
            {
                type: 'og:title',
                value: data.title ?? this.title.getTitle()
            },
            {
                type: 'og:description',
                value: data.description
            },
            {
                type: 'og:url',
                value: pageUrl
            },
            {
                type: 'og:image',
                value: data.image?.src
            },
            {
                type: 'og:type',
                value: template === PageTypes.ArticlePage ? 'article' : 'website'
            },
            {
                type: 'fb:app_id',
                value: data.app_id
            }
        );

        // GA
        this.gtmService.trackPageViewEvent(pageUrl, this.title.getTitle(), data.contentTypeAlias);

        // SEO meta
        this.metaService.setNoIndex(data.hideFromGoogle || !environment.production);
        this.metaService.setCanonical(data.canonicalUrl?.url);

        // Set title and meta description to all pages except Product Detail Page
        // Product Detail Page does this dynamically
        if (template !== PageTypes.ProductDetailPage) {
            this.metaService.setTitle(data.metaTitle ?? data.name);
            this.metaService.setDescription(data.metaDescription);
        }

        // Relewise tracking
        if (template === PageTypes.BrandPage && data.properties?.brand) {
            this.relewiseTrackingService.trackBrand(data.properties.brand);
        }
        if (template === PageTypes.ProductFilterPage && data.key) {
            this.relewiseTrackingService.trackCategory(data.key);
        }
    }
}
